import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { FIRM, WHATSAPP_URL, SERVICES, DISCLAIMER_TEXT, ICAI_NOTE } from "../data/content";
import { Logo } from "./Navbar";

export function Footer() {
  return (
    <footer className="relative bg-navy-dark text-slate-300 overflow-hidden" data-testid="site-footer">
      <div className="grain-overlay" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <Logo dark />
            <p className="mt-5 text-sm text-slate-400 leading-relaxed">{FIRM.tagline}</p>
            <p className="mt-4 text-xs font-mono uppercase tracking-[0.25em] text-gold">{FIRM.domain}</p>
          </div>
          <div>
            <h4 className="text-white font-serif text-lg font-semibold mb-5">Quick Links</h4>
            <ul className="space-y-2.5 text-sm">
              {[
                ["About Us", "/about"],
                ["Our Services", "/services"],
                ["Industries Served", "/industries"],
                ["Knowledge Centre", "/knowledge-centre"],
                ["Contact Us", "/contact"],
                ["Book a Consultation", "/contact"],
              ].map(([label, to]) => (
                <li key={label}>
                  <Link to={to} className="hover:text-gold transition-colors" data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-white font-serif text-lg font-semibold mb-5">Services</h4>
            <ul className="space-y-2.5 text-sm">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services#${s.slug}`} className="hover:text-gold transition-colors" data-testid={`footer-service-${s.slug}`}>
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-white font-serif text-lg font-semibold mb-5">Contact</h4>
            <ul className="space-y-3.5 text-sm">
              <li className="flex gap-3">
                <MapPin size={16} className="text-gold shrink-0 mt-0.5" />
                <span>{FIRM.addressLines.join(", ")}</span>
              </li>
              <li>
                <a href={`tel:${FIRM.phone1Raw}`} className="flex gap-3 hover:text-gold transition-colors" data-testid="footer-phone-1">
                  <Phone size={16} className="text-gold shrink-0 mt-0.5" /> {FIRM.phone1}
                </a>
              </li>
              <li>
                <a href={`tel:${FIRM.phone2Raw}`} className="flex gap-3 hover:text-gold transition-colors" data-testid="footer-phone-2">
                  <Phone size={16} className="text-gold shrink-0 mt-0.5" /> {FIRM.phone2}
                </a>
              </li>
              <li>
                <a href={`mailto:${FIRM.email}`} className="flex gap-3 hover:text-gold transition-colors break-all" data-testid="footer-email">
                  <Mail size={16} className="text-gold shrink-0 mt-0.5" /> {FIRM.email}
                </a>
              </li>
              <li>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex gap-3 hover:text-gold transition-colors" data-testid="footer-whatsapp">
                  <MessageCircle size={16} className="text-gold shrink-0 mt-0.5" /> WhatsApp Us
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 space-y-4">
          <p className="text-xs leading-relaxed text-slate-500" data-testid="footer-disclaimer">{DISCLAIMER_TEXT}</p>
          <p className="text-xs leading-relaxed text-slate-500">{ICAI_NOTE}</p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4">
            <p className="text-xs text-slate-500">
              © {new Date().getFullYear()} {FIRM.name}, {FIRM.qualification}. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs">
              <Link to="/privacy-policy" className="hover:text-gold transition-colors" data-testid="footer-link-privacy-policy">Privacy Policy</Link>
              <Link to="/terms-and-conditions" className="hover:text-gold transition-colors" data-testid="footer-link-terms">Terms &amp; Conditions</Link>
              <Link to="/disclaimer" className="hover:text-gold transition-colors" data-testid="footer-link-disclaimer">Disclaimer</Link>
              <Link to="/cookie-policy" className="hover:text-gold transition-colors" data-testid="footer-link-cookie-policy">Cookie Policy</Link>
              <Link to="/admin" className="hover:text-gold transition-colors" data-testid="footer-link-admin">Admin Login</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
